import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, ArrowDown, ChevronUp, ChevronDown } from 'lucide-react';
import Select from 'react-select';
import DateRangeFilter from './DateRangeFilter';
import { frozenColumnStyle } from '../constants/MasterSpreadsheetStyles';

const TableHeader = ({
  getVisibleColumns,
  visibleColumnNames,
  columnWidths,
  defaultColumnWidth,
  sortColumn,
  sortDirection,
  handleSort,
  columnFilters,
  columnFilterOptions,
  handleFilterChange,
  selectRefs
}) => {
  return (
    <thead 
      className="sticky top-0 z-40 border-b-2 border-slate-300 shadow-lg"
      style={{
        transform: 'translate3d(0, 0, 0)',
        willChange: 'transform, top',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        perspective: 1000,
        WebkitPerspective: 1000,
        contain: 'layout style paint',
        isolation: 'isolate',
        zIndex: 40,
        backgroundColor: '#f8fafc',
        backgroundImage: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      }}
    >
      <tr>
        <th
          className="relative group"
          style={{
            ...frozenColumnStyle,
            zIndex: 50,
            background: '#f8fafc',
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid rgba(226,232,240,0.8)',
            width: '60px',
            minWidth: '60px',
            isolation: 'isolate',
          }}
        >
          <div className="p-4 flex items-center justify-center">
            {/* Empty header cell for row numbers */}
          </div>
          <div
            className="absolute right-0 top-0 w-0.5 h-full cursor-col-resize col-resize-handle 
              opacity-0 bg-gradient-to-b from-transparent via-blue-400 to-transparent"
          ></div>
        </th>
        {Object.keys(getVisibleColumns()).map((key, index) => (
          <th
            key={key}
            className={`relative group ${
              key === 'Company Name' ? 'frozen-column-cell' : ''
            }`}
            style={{
              ...(key === 'Company Name' ? {
                ...frozenColumnStyle,
                background: '#f8fafc',
                backgroundColor: '#f8fafc',
                zIndex: 50,
                isolation: 'isolate',
              } : {
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              }),
              width: columnWidths[key] !== undefined ? columnWidths[key] : defaultColumnWidth,
              maxWidth: columnWidths[key] !== undefined ? columnWidths[key] : defaultColumnWidth,
              borderBottom: '1px solid rgba(226,232,240,0.8)',
            }}
          >
            <div
              className="flex items-center justify-center cursor-pointer px-6 py-5
                hover:bg-slate-100/50 
                transition-colors duration-200 relative group"
              onClick={() => handleSort(key)}
              title={`Sort by ${visibleColumnNames[key]}`}
            >
              <div className="truncate font-semibold text-slate-700 text-sm tracking-wide uppercase">
                {visibleColumnNames[key]}
              </div>
              <div className="ml-3 flex items-center">
                {sortColumn === key ? (
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    {sortDirection === 'asc' ? (
                      <ArrowUp size={16} className="text-blue-600" strokeWidth={2.5} />
                    ) : (
                      <ArrowDown size={16} className="text-blue-600" strokeWidth={2.5} />
                    )}
                  </motion.div>
                ) : (
                  <div className="opacity-0 group-hover:opacity-60 transition-all duration-300 flex flex-col">
                    <ChevronUp size={14} className="text-slate-400 -mb-2" />
                    <ChevronDown size={14} className="text-slate-400" />
                  </div>
                )}
              </div>
              {sortColumn === key && (
                <motion.div 
                  layoutId="activeSortIndicator"
                  className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                  style={{
                    background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </div>
            <div
              className="absolute right-0 top-0 w-0.5 h-full cursor-col-resize col-resize-handle 
                opacity-0 hover:opacity-100 bg-gradient-to-b from-transparent via-blue-400 to-transparent 
                transition-all duration-300"
            ></div>
          </th>
        ))}
      </tr>
      <tr>
        <th 
          className="p-2"
          style={{
            ...frozenColumnStyle,
            zIndex: 50,
            background: '#f8fafc',
            backgroundColor: '#f8fafc',
            isolation: 'isolate',
          }}
        ></th>
        {Object.keys(getVisibleColumns()).map((key, index) => (
          <th
            key={key}
            className={`p-2 ${
              key === 'Company Name' ? 'frozen-column-cell' : ''
            }`}
            style={{
              ...(key === 'Company Name' ? {
                ...frozenColumnStyle,
                background: '#f8fafc',
                backgroundColor: '#f8fafc',
                borderRight: '2px solid #e2e8f0',
                zIndex: 50,
                isolation: 'isolate',
              } : {
                background: 'rgba(248,250,252,0.95)',
              }),
              width: columnWidths[key] !== undefined ? columnWidths[key] : defaultColumnWidth,
              maxWidth: columnWidths[key] !== undefined ? columnWidths[key] : defaultColumnWidth,
              backdropFilter: 'blur(10px)',
            }}
          >
            <div ref={(el) => (selectRefs.current[key] = el)}>
              {columnFilterOptions[key]?.isDateFilter ? (
                <DateRangeFilter
                  value={columnFilters[key]}
                  onChange={(dates) => handleFilterChange(key, dates)}
                  placeholder={`Filter ${visibleColumnNames[key]}...`}
                />
              ) : (
                <Select
                  isMulti
                  options={columnFilterOptions[key]}
                  value={
                    columnFilters[key]?.map((value) => ({
                      value,
                      label: String(value),
                    })) || []
                  }
                  onChange={(selectedOptions) =>
                    handleFilterChange(key, selectedOptions?.map(opt => opt.value) || [])
                  }
                  placeholder="Filter..."
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: '38px',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderColor: state.isFocused ? 'rgba(99, 102, 241, 0.5)' : 'rgba(226, 232, 240, 0.8)',
                      boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.1)' : 'none',
                      borderRadius: '0.75rem',
                      backdropFilter: 'blur(8px)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'rgba(99, 102, 241, 0.4)',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      },
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '2px 8px',
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: 'rgba(219, 234, 254, 0.7)',
                      borderRadius: '0.5rem',
                      margin: '2px',
                      border: '1px solid rgba(147, 197, 253, 0.5)',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      fontSize: '0.75rem',
                      padding: '2px 6px',
                      color: '#1e40af',
                      fontWeight: '500',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#3b82f6',
                      ':hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                      },
                      borderRadius: '0 0.5rem 0.5rem 0',
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      borderRadius: '0.75rem',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      overflow: 'hidden',
                      zIndex: 50,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: isSelected 
                        ? 'rgba(99, 102, 241, 0.9)' 
                        : isFocused 
                          ? 'rgba(99, 102, 241, 0.1)' 
                          : 'transparent',
                      color: isSelected ? 'white' : '#334155',
                      ':active': {
                        backgroundColor: 'rgba(99, 102, 241, 0.8)',
                        color: 'white',
                      },
                      padding: '10px 14px',
                      fontSize: '0.875rem',
                      transition: 'all 0.15s ease',
                      cursor: 'pointer',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#94a3b8',
                      fontSize: '0.875rem',
                    }),
                    input: (base) => ({
                      ...base,
                      fontSize: '0.875rem',
                      color: '#334155',
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      padding: '4px',
                      color: '#94a3b8',
                      ':hover': {
                        color: '#475569',
                      },
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      padding: '4px',
                      color: '#94a3b8',
                      ':hover': {
                        color: '#475569',
                      },
                    }),
                  }}
                  components={{
                    IndicatorSeparator: () => null, // Remove the separator between indicators
                  }}
                />
              )}
            </div>
          </th>
        ))}
      </tr>
    </thead>
  );
};

export default TableHeader;